<template>
    <v-card>
        <v-card-title class="text-h5">
            <v-btn
                class="mr-3"
                depressed
                color="primary"
                icon
                rounded
                @click="close"
            >
                <v-icon>mdi-close</v-icon>
            </v-btn>
            GET ASSEMBLY COMPONENTS</v-card-title
        >
        <v-divider class="ml-4 mr-5" />
        <v-card-text>
            <v-form v-model="valid" class="mb-2 mx-1 mt-3">
                <v-data-table
                    id="virtual-scroll-table"
                    disable-pagination
                    hide-default-footer
                    :headers="filteredHeaders"
                    :items="filteredStockEntries"
                    class="elevation-0"
                    :mobile-breakpoint="0"
                    fixed-header
                    :style="{ width: '100%' }"
                    :height="$vuetify.breakpoint.mdAndDown ? '70vh' : '65vh'"
                    :loading="loading"
                >
                    <!--HEADERS-->
                    <template v-slot:[`header.partNumber`]="{ header }">
                        <v-text-field
                            class="py-2 mt-2 custom-label"
                            :label="header.text"
                            v-model="partNumberToFilter"
                            dense
                        />
                    </template>
                    <template v-slot:[`header.checked`]>
                        <div class="d-flex justify-center">
                            <v-checkbox
                                v-model="allFilteredSelected"
                                class="ma-0 pa-0"
                                hide-details
                                :indeterminate="hasPartialSelection"
                            />
                        </div>
                    </template>
                    <!--ITEMS-->
                    <template v-slot:[`item.checked`]="{ item }">
                        <div class="d-flex justify-center align-center">
                            <v-icon
                                v-if="item.selectable === false"
                                color="error"
                                small
                            >
                                mdi-close
                            </v-icon>
                            <v-checkbox
                                v-else
                                :value="item.checked"
                                @change="handleEntryCheck(item, $event)"
                                class="ma-0 pa-0"
                                hide-details
                            />
                        </div>
                    </template>
                </v-data-table>
            </v-form>
        </v-card-text>
        <v-card-actions class="pb-3 pt-0">
            <v-spacer></v-spacer>
            <div
                v-if="batchProgress.isProcessing"
                class="d-flex align-center mr-4"
            >
                <span class="text-caption">
                    Processing batch {{ batchProgress.current }} of
                    {{ batchProgress.total }}
                </span>
            </div>
            <v-btn
                color="primary"
                :disabled="buttonConditions()"
                @click="save"
                :loading="loading"
            >
                <v-icon v-if="batchProgress.isProcessing" left
                    >mdi-loading</v-icon
                >
                {{ batchProgress.isProcessing ? 'PROCESSING...' : 'Confirm' }}
            </v-btn>
        </v-card-actions>
    </v-card>
</template>

<script>
import API from '@/services/api'
import { mapMutations } from 'vuex'

export default {
    name: 'AssemblyComponents',
    components: {},
    props: {
        productionInfo: {
            type: Array,
            required: true,
        },
        itemsToProduce: {
            type: Array,
            required: false,
            default: () => [],
        },
        workOrder: {
            type: Object,
            required: true,
        },
        selectedProcess: {
            type: Object,
            required: true,
        },
    },
    data: () => ({
        valid: false,
        loading: false,
        batchSize: 100,
        batchProgress: {
            current: 0,
            total: 0,
            isProcessing: false,
        },
        headers: [
            {
                text: 'PART NUMBER',
                value: 'partNumber',
                align: 'center',
                sortable: false,
                width: '200px',
            },
            {
                text: 'WORK ORDER',
                value: 'workOrder',
                align: 'center',
                sortable: false,
                width: '150px',
            },
            {
                text: 'AVAILABLE QTY',
                value: 'availableQty',
                align: 'center',
                sortable: false,
            },
            {
                text: 'REQUIRED QTY',
                value: 'requiredQty',
                align: 'center',
                sortable: false,
            },
            {
                text: 'CHECKED',
                value: 'checked',
                align: 'center',
                sortable: false,
            },
        ],
        stockEntries: [],
        stockEntriesPerPartnumber: [],
        partNumberToFilter: null,
        formattedStock: {},
        partNumbers: {},
        restrictionDate: '2026-02-15',
    }),
    computed: {
        filteredHeaders() {
            // Si se puede hacer bypass, ocultar las columnas WORK ORDER, CHECKED y AVAILABLE QTY
            if (this.canBypassStockSelection) {
                return this.headers.filter(
                    header =>
                        header.value !== 'workOrder' &&
                        header.value !== 'checked' &&
                        header.value !== 'availableQty'
                )
            }
            return this.headers
        },
        filteredStockEntries() {
            let conditions = []

            if (this.partNumberToFilter) {
                conditions.push(this.filterForPartNumber)
            }

            if (conditions.length > 0) {
                return this.stockEntries.filter(item => {
                    return conditions.every(condition => {
                        return condition(item)
                    })
                })
            }
            return this.stockEntries
        },
        allFilteredSelected: {
            get() {
                const selectableEntries = this.filteredStockEntries.filter(
                    entry => entry.selectable !== false
                )

                if (selectableEntries.length === 0) {
                    return false
                }

                return selectableEntries.every(entry => entry.checked)
            },
            set(value) {
                this.filteredStockEntries.forEach(entry => {
                    if (entry.selectable !== false) {
                        entry.checked = value
                        // Sync with stockEntriesPerPartnumber
                        this.syncCheckedState(
                            entry.partNumber,
                            entry.destinationPartnumber,
                            value
                        )
                    }
                })
            },
        },
        hasPartialSelection() {
            const selectableEntries = this.filteredStockEntries.filter(
                entry => entry.selectable !== false
            )

            if (selectableEntries.length === 0) {
                return false
            }

            const checkedCount = selectableEntries.filter(
                entry => entry.checked
            ).length

            return checkedCount > 0 && checkedCount < selectableEntries.length
        },
        canBypassStockSelection() {
            // Permitir bypass si es reproceso con garantía o si no hay originWorkOrders y la fecha es anterior a la restricción
            const isReprocessWarrantyAssembly =
                this.workOrder?.assemblyWorkOrder &&
                this.workOrder?.reprocess &&
                this.workOrder?.warranty
            const hasNoOriginWorkOrders =
                !this.workOrder.originWorkOrders ||
                (Array.isArray(this.workOrder.originWorkOrders) &&
                    this.workOrder.originWorkOrders.length === 0) ||
                (typeof this.workOrder.originWorkOrders === 'object' &&
                    Object.keys(this.workOrder.originWorkOrders).length === 0)

            const restrictionDate = new Date(this.restrictionDate)
            const currentDate = new Date()

            // Verificar si la fecha de aprobación es menor a la restricción
            const approvedTimestamp =
                this.workOrder.approvedDate?.seconds ||
                this.workOrder.approvedDate?._seconds

            const isApprovedBeforeRestriction = approvedTimestamp
                ? new Date(approvedTimestamp * 1000) < restrictionDate
                : false

            // Si no hay originWorkOrders o la fecha es anterior a la restricción o la fecha de aprobación es menor a la restricción, permitir bypass
            return (
                isReprocessWarrantyAssembly ||
                (hasNoOriginWorkOrders && currentDate < restrictionDate) ||
                isApprovedBeforeRestriction
            )
        },
    },

    async mounted() {
        try {
            this.loading = true

            //get project stock
            this.partNumbers = await API.getPartNumbersInKeys({
                projectId: this.workOrder.projectId,
            })

            this.formatStock()

            let productionItems = {}
            //calculate total components required
            // Only process items that have productionQty > 0 (selected items)
            this.productionInfo
                .filter(data => data.productionQty > 0)
                .forEach(data => {
                    if (data.item && data.item.assemblyItems) {
                        // Initialize productionItems for this partNumberId before processing assembly items
                        if (!productionItems[data.item.partNumberId]) {
                            productionItems[data.item.partNumberId] = {
                                requiredPartNumbers: {},
                                partNumberId: data.item.partNumberId,
                            }
                        }

                        data.item.assemblyItems.forEach(ai => {
                            // WO items from WOMethods.formatItems always have partNumberId (object key).
                            // Stored assembly rows may omit the string `partNumber` (code); resolve from project part numbers.
                            const resolvedPartNumberCode =
                                ai.partNumber ||
                                (ai.partNumberId &&
                                    this.partNumbers?.[ai.partNumberId]
                                        ?.code) ||
                                ''

                            // Get the partNumberId for this component
                            // First try from formattedStock, then search in partNumbers
                            let componentPartNumberId =
                                (resolvedPartNumberCode &&
                                    this.formattedStock[resolvedPartNumberCode]
                                        ?.partNumberId) ||
                                null

                            // If not found in formattedStock, search in partNumbers by code
                            if (
                                !componentPartNumberId &&
                                this.partNumbers &&
                                resolvedPartNumberCode
                            ) {
                                const foundPartNumber = Object.keys(
                                    this.partNumbers
                                ).find(
                                    partNumberId =>
                                        this.partNumbers[partNumberId]?.code ===
                                        resolvedPartNumberCode
                                )
                                if (foundPartNumber) {
                                    componentPartNumberId = foundPartNumber
                                }
                            }

                            if (
                                !componentPartNumberId &&
                                ai.partNumberId &&
                                this.partNumbers?.[ai.partNumberId]
                            ) {
                                componentPartNumberId = ai.partNumberId
                            }

                            // Always add to productionItems, even if partNumberId is null
                            // We'll handle it in formatStockEntries
                            const partNumberIdKey =
                                componentPartNumberId ||
                                ai.partNumberId ||
                                resolvedPartNumberCode

                            // Calculate quantity needed for this production item
                            const qtyNeeded =
                                ai.qtyPerAssembly * data.productionQty

                            // Save the required partnumbers with quantities by production item
                            if (
                                !productionItems[data.item.partNumberId]
                                    .requiredPartNumbers[partNumberIdKey]
                            ) {
                                productionItems[
                                    data.item.partNumberId
                                ].requiredPartNumbers[partNumberIdKey] = {
                                    qty: 0,
                                    partNumberId: componentPartNumberId,
                                    partNumber:
                                        resolvedPartNumberCode ||
                                        ai.partNumberId,
                                }
                            }
                            productionItems[
                                data.item.partNumberId
                            ].requiredPartNumbers[partNumberIdKey].qty +=
                                +qtyNeeded || 0
                        })
                    }
                })

            this.formatStockEntries(productionItems)
        } catch (error) {
            this.setErrorItems({
                source: this.$options.name,
                message: error.message,
            })
        } finally {
            this.loading = false
        }
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        async save() {
            try {
                this.loading = true

                // Group checked stock entries by destination partNumberId
                const stockEntriesByDestination = {}
                this.stockEntriesPerPartnumber
                    .filter(entry => entry.checked)
                    .forEach(entry => {
                        if (!entry.destinationPartnumber) {
                            return
                        }

                        if (
                            !stockEntriesByDestination[
                                entry.destinationPartnumber
                            ]
                        ) {
                            stockEntriesByDestination[
                                entry.destinationPartnumber
                            ] = []
                        }

                        stockEntriesByDestination[
                            entry.destinationPartnumber
                        ].push({
                            partNumber: entry.partNumber,
                            partNumberId: entry.partNumberId,
                            workOrder: entry.workOrder,
                            availableQty: entry.availableQty,
                            requiredQty: entry.requiredQty,
                            deliveries: entry.deliveries,
                        })
                    })

                // Prepare items for batch processing
                // Each item gets only its own stock entries
                const itemsToRegister = this.productionInfo
                    .filter(data => data.productionQty > 0)
                    .map(data => ({
                        partNumberId: data.item.partNumberId,
                        process: this.selectedProcess,
                        qty: data.productionQty,
                        machine: data.selectedMachine,
                        stockEntries:
                            stockEntriesByDestination[data.item.partNumberId] ||
                            [],
                    }))

                // Process items in batches
                const totalBatches = Math.ceil(
                    itemsToRegister.length / this.batchSize
                )

                // Initialize batch progress
                this.batchProgress = {
                    current: 0,
                    total: totalBatches,
                    isProcessing: true,
                }

                let finalPartNumbers = null
                const allResults = []
                const allErrors = []

                for (
                    let i = 0;
                    i < itemsToRegister.length;
                    i += this.batchSize
                ) {
                    const batch = itemsToRegister.slice(i, i + this.batchSize)
                    const currentBatchNumber =
                        Math.floor(i / this.batchSize) + 1

                    // Update progress
                    this.batchProgress.current = currentBatchNumber

                    try {
                        const batchResult = await API.registerAssemblyProductionBatch(
                            {
                                workOrderId: this.workOrder.id,
                                items: batch,
                            }
                        )

                        // Accumulate results
                        if (batchResult.results) {
                            allResults.push(...batchResult.results)
                        }
                        if (batchResult.errors) {
                            allErrors.push(...batchResult.errors)
                        }

                        // Keep the latest partNumbers (they should be the same for all batches)
                        if (batchResult.partNumbers) {
                            finalPartNumbers = batchResult.partNumbers
                        }

                        // Optional: Add a small delay between batches
                        if (i + this.batchSize < itemsToRegister.length) {
                            await new Promise(resolve =>
                                setTimeout(resolve, 100)
                            )
                        }
                    } catch (error) {
                        this.setErrorItems({
                            source: this.$options.name,
                            message: error.message,
                        })
                    }
                }

                // Reset batch progress
                this.batchProgress.isProcessing = false

                // register the items that are producing other processes
                if (this.itemsToProduce?.length > 0) {
                    await this.$emit('registerItems', this.itemsToProduce)
                }

                // Emit the final result
                this.$emit(
                    'closeAll',
                    finalPartNumbers || this.workOrder.partNumbers
                )
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
                this.batchProgress.isProcessing = false
            }
        },
        close() {
            this.$emit('closeDialog')
        },
        formatStock() {
            let formattedStock = {}

            if (
                !this.partNumbers ||
                Object.keys(this.partNumbers).length === 0
            ) {
                return formattedStock
            }

            for (const partNumberId of Object.keys(this.partNumbers)) {
                const partNumberCode = this.partNumbers[partNumberId].code

                formattedStock[partNumberCode] = { partNumberId }

                //if the partNumber has deliveries (stock)
                if (this.partNumbers[partNumberId].deliveries) {
                    this.partNumbers[partNumberId].deliveries.forEach(
                        delivery => {
                            if (
                                (
                                    this.workOrder.originWorkOrders || []
                                ).includes(delivery.originWorkOrder)
                            ) {
                                if (
                                    !formattedStock[partNumberCode][
                                        delivery.originWorkOrder
                                    ]
                                ) {
                                    formattedStock[partNumberCode][
                                        delivery.originWorkOrder
                                    ] = [delivery]
                                } else {
                                    formattedStock[partNumberCode][
                                        delivery.originWorkOrder
                                    ].push(delivery)
                                }
                            }
                        }
                    )
                }
            }
            this.formattedStock = formattedStock
        },
        formatStockEntries(productionItems) {
            let stockEntries = []

            // Process each production item and create stock entries for its required components
            for (const [
                destinationPartNumberId,
                productionItem,
            ] of Object.entries(productionItems)) {
                // Process each component required by this production item
                for (const [componentKey, componentData] of Object.entries(
                    productionItem.requiredPartNumbers
                )) {
                    // Extract data from the structure
                    const requiredQty = componentData.qty || 0
                    const componentPartNumberId =
                        componentData.partNumberId || null
                    const componentPartNumber =
                        componentData.partNumber ||
                        Object.keys(this.formattedStock).find(
                            code =>
                                this.formattedStock[code]?.partNumberId ===
                                componentPartNumberId
                        ) ||
                        componentKey

                    let remainingQty = requiredQty

                    // Get stock information for this component
                    const stockForPartNumber =
                        this.formattedStock[componentPartNumber] || {}
                    // Extract workOrderMap, excluding partNumberId
                    const workOrderMap = { ...stockForPartNumber }
                    delete workOrderMap.partNumberId

                    // Calculate available stock per work order
                    const workOrderStocks = Object.entries(workOrderMap)
                        .map(([workOrder, deliveries]) => {
                            const totalAvailable = deliveries.reduce(
                                (accumulator, delivery) =>
                                    accumulator + (Number(delivery.qty) || 0),
                                0
                            )

                            return {
                                workOrder,
                                deliveries,
                                totalAvailable,
                            }
                        })
                        .filter(stock => stock.totalAvailable > 0)

                    // Create stock entries from available stock for this production item
                    for (const stock of workOrderStocks) {
                        if (remainingQty <= 0) {
                            break
                        }

                        const qtyToUse = Math.min(
                            stock.totalAvailable,
                            remainingQty
                        )

                        stockEntries.push({
                            partNumber: componentPartNumber,
                            partNumberId: componentPartNumberId,
                            destinationPartnumber: destinationPartNumberId,
                            workOrder: stock.workOrder,
                            availableQty: stock.totalAvailable,
                            requiredQty: qtyToUse,
                            checked: false,
                            deliveries: stock.deliveries,
                            selectable: true,
                        })

                        remainingQty -= qtyToUse
                    }

                    // Always add entry for remaining quantity (even if 0, to show items without stock)
                    // If there's no stock at all, remainingQty will equal requiredQty
                    if (remainingQty > 0 || workOrderStocks.length === 0) {
                        stockEntries.push({
                            partNumber: componentPartNumber,
                            partNumberId: componentPartNumberId,
                            destinationPartnumber: destinationPartNumberId,
                            workOrder: null,
                            availableQty: 0,
                            requiredQty:
                                remainingQty > 0 ? remainingQty : requiredQty,
                            checked: false,
                            deliveries: [],
                            selectable: false,
                        })
                    }
                }
            }

            // Store entries per partnumber (for backend)
            this.stockEntriesPerPartnumber = stockEntries

            // Create aggregated stockEntries for presentation (grouped by partNumber, no duplicates)
            this.stockEntries = this.createAggregatedStockEntries(stockEntries)
        },
        createAggregatedStockEntries(stockEntriesPerPartnumber) {
            const aggregated = {}

            stockEntriesPerPartnumber.forEach(entry => {
                // Group by partNumber AND destinationPartnumber to avoid mixing components from different items
                const key = `${
                    entry.partNumber
                }_${entry.destinationPartnumber || 'none'}`

                if (!aggregated[key]) {
                    aggregated[key] = {
                        partNumber: entry.partNumber,
                        partNumberId: entry.partNumberId,
                        destinationPartnumber: entry.destinationPartnumber,
                        availableQty: entry.availableQty,
                        requiredQty: 0,
                        checked: entry.checked,
                        selectable: entry.selectable,
                        workOrder: entry.workOrder,
                        deliveries: entry.deliveries,
                    }
                }

                // Sum required quantities
                aggregated[key].requiredQty += entry.requiredQty

                // Update checked state (if any is checked, mark as checked)
                if (entry.checked) {
                    aggregated[key].checked = true
                }

                // Update selectable (if any is not selectable, mark as not selectable)
                if (entry.selectable === false) {
                    aggregated[key].selectable = false
                }
            })

            return Object.values(aggregated)
        },
        syncCheckedState(partNumber, destinationPartnumber, checked) {
            // Sync checked state from aggregated entry to all corresponding entries in stockEntriesPerPartnumber
            this.stockEntriesPerPartnumber.forEach(entry => {
                if (
                    entry.partNumber === partNumber &&
                    entry.destinationPartnumber === destinationPartnumber
                ) {
                    entry.checked = checked
                }
            })
        },
        handleEntryCheck(entry, checked) {
            // Update the aggregated entry
            entry.checked = checked
            this.syncCheckedState(
                entry.partNumber,
                entry.destinationPartnumber,
                checked
            )
        },
        filterForPartNumber(item) {
            return (
                item.partNumber &&
                item.partNumber
                    .toLowerCase()
                    .includes(this.partNumberToFilter.toLowerCase())
            )
        },
        buttonConditions() {
            // Si se puede hacer bypass, habilitar el botón
            if (this.canBypassStockSelection) {
                return this.batchProgress.isProcessing
            }

            // Validación normal: todos los entries deben estar checked
            return (
                !this.stockEntries.every(entry => entry.checked) ||
                this.batchProgress.isProcessing
            )
        },
    },
}
</script>

<style>
#virtual-scroll-table {
    width: 100%;
    overflow: auto;
}
.v-data-table {
    border: 1px solid #eeeeee;
}
.v-data-table .v-data-table {
    width: 100%;
}
</style>
